import React from 'react';
import { Card, CardContent, Typography } from '@material-ui/core';
import { PausedChip, double, long, percent } from './utils';

/**
 * Delivery summary for a single campaign: one card per time grain.
 *
 * Replaces the 5x4 counter table that used to sit on the campaign Settings
 * tab (NBU-4). That table rendered 20 values in unlabelled columns whose
 * meaning changed from row to row - column 2 was impressions on one row, a
 * money metric on the next, a LIMIT on the next - so every cell had to be
 * re-read. It also drew progress bars without ever showing the denominator
 * they were a percentage OF.
 *
 * The rule here: every card answers one question ("how is this grain pacing
 * against its cap?"), and no number appears without the context that makes it
 * readable. A percentage always sits next to the cap it divides by; a grain
 * with no cap says so in words instead of drawing an empty bar.
 *
 * All values come off the campaign record itself, written by the insights
 * budget coordinator ETL (service/etl/budget_coordinator.js and its ClickHouse
 * twin). They are per-cycle snapshots, not live counters. Spend is GROSS - the
 * same `cp` lineage the spend limits are expressed in - so the figures and the
 * caps reconcile by construction. See SpendPacingChart for the shape over time.
 */

// Within cap vs over cap. Same two signals utils.progressBar uses, so the
// advertiser tables on the same screen keep reading the same way.
const COLOR_WITHIN = '#49b27b';
const COLOR_OVER = 'orangered';
const COLOR_TRACK = '#3f3f3f';
const COLOR_MUTED = '#9e9e9e';

// Deliberately NOT SpendPacingChart's barColor(): that function grades a
// single time bucket against its per-bucket target, where sitting under 60%
// means under-delivering. These bars are cumulative progress through a period,
// where being early and low is normal, not a warning.

/**
 * The grains rendered, in order. Adding one is a config entry, not new JSX.
 *
 * `clicks: null` means the grain has no clicks counter on the record, so the
 * card omits the clicks line rather than printing a misleading zero.
 * `activeFlags` are the campaign's `*_limit_active` booleans that belong to
 * this grain; any of them being explicitly false pauses the card.
 */
export const GRAINS = [
  {
    key: 'hour',
    title: 'This hour',
    spend: 'current_spend',
    cap: 'hourly_revenue_limit',
    impressions: 'current_impressions',
    clicks: 'current_clicks',
    // No hourly_revenue_limit_active exists on the record - the hourly cap is
    // enforced through the impressions quota, not a pause flag.
    activeFlags: []
  },
  {
    key: 'today',
    title: 'Today',
    spend: 'current_daily_revenue',
    cap: 'daily_revenue_limit',
    impressions: 'current_daily_impressions',
    clicks: 'current_daily_clicks',
    activeFlags: ['daily_revenue_limit_active', 'daily_clicks_limit_active']
  },
  {
    key: 'lifetime',
    title: 'Lifetime',
    spend: 'current_lifetime_revenue',
    cap: 'lifetime_revenue_limit',
    impressions: 'current_lifetime_impressions',
    impressionsCap: 'lifetime_impressions_limit',
    clicks: null,
    activeFlags: ['lifetime_revenue_limit_active', 'lifetime_impressions_limit_active']
  }
];

export function findGrain (key) {
  return GRAINS.find(g => g.key === key);
}

/** Numeric coercion. ClickHouse UInt64 columns arrive as strings. */
function num (value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** A limit is "set" only when positive. Zero and absent both mean no cap. */
function limit (record, field) {
  if (!field) return null;
  const n = num(record[field]);
  return n > 0 ? n : null;
}

/**
 * Everything one card needs, derived from the record. Pure - the rendering
 * below reads these fields and formats them, and makes no decisions of its own.
 */
export function grainStats (record, grain) {
  const rec = record || {};
  const spend = num(rec[grain.spend]);
  const cap = limit(rec, grain.cap);
  const impressions = num(rec[grain.impressions]);
  const clicks = grain.clicks ? num(rec[grain.clicks]) : null;

  return {
    key: grain.key,
    title: grain.title,
    spend,
    cap,
    hasCap: cap !== null,
    pct: cap === null ? null : (100 * spend) / cap,
    overCap: cap !== null && spend > cap,
    impressions,
    impressionsCap: limit(rec, grain.impressionsCap),
    clicks,
    // Guard the divide: a grain that served nothing has no CTR, not a zero
    // one, and definitely not NaN.
    ctr: (clicks === null || impressions <= 0) ? null : clicks / impressions,
    // Only an EXPLICIT false is a pause. An absent flag means the budget
    // coordinator has not written this campaign yet, which is unknown rather
    // than exhausted - the old pausedIndicator flagged that case as PAUSED.
    paused: (grain.activeFlags || []).some(flag => rec[flag] === false)
  };
}

/** Bar fill width, clamped so an over-cap bar cannot overflow its track. */
export function barWidth (pct) {
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return Math.min(100, pct);
}

export function capColor (pct) {
  return Number.isFinite(pct) && pct > 100 ? COLOR_OVER : COLOR_WITHIN;
}

/** Progress against a cap, or the words "no cap" when there is nothing to divide by. */
const CapBar = ({ stats }) => {
  if (!stats.hasCap) {
    return (
      <div style={{ height: '18px', display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" style={{ color: COLOR_MUTED, fontStyle: 'italic' }}>
          {/* The default reads as an instruction: set a cap. Right for a grain
              that could have one, wrong for the selected-range card, whose caps
              exist but do not bound an arbitrary window. */}
          {stats.capNote || 'no spend cap set'}
        </Typography>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '18px' }}>
      <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: COLOR_TRACK, overflow: 'hidden' }}>
        <div style={{ width: `${barWidth(stats.pct)}%`, height: '100%', background: capColor(stats.pct) }} />
      </div>
      <Typography variant="caption" style={{ color: capColor(stats.pct), fontWeight: 700, minWidth: '44px', textAlign: 'right' }}>
        {percent(stats.pct)}
      </Typography>
    </div>
  );
};

const SecondaryLine = ({ children }) => (
  <Typography variant="body2" style={{ color: COLOR_MUTED, lineHeight: 1.7 }}>{children}</Typography>
);

// Cards size to their CONTENT, not to the viewport. `1 1 240px` let each card
// grow to a third of a wide screen - roughly 600px holding 200px of text, with
// a progress bar stretched so far that the fill was hard to see at all.
// `0 1 <basis>` keeps them at the basis and lets them shrink and wrap on
// narrow screens instead.
const CARD_WIDTH = 260;
const CARD_GAP = 16;

/**
 * Natural width of a row of `count` cards, gaps included.
 *
 * Exported because the performance panel sizes ITSELF from this: the chart above
 * is 100% of the panel, so unless the panel is exactly as wide as this row, the
 * chart's right edge overshoots the last card. It did - the panel was a viewport
 * percentage and the row a fixed max, so the two only agreed by coincidence.
 * One number, both consumers.
 */
export function cardsRowWidth (count) {
  const n = Math.max(1, count);
  return n * CARD_WIDTH + (n - 1) * CARD_GAP;
}

/**
 * The content column at the top of the campaign Settings tab, as a CSS width.
 *
 * The cards row is what defines it - it is the one element up there with a fixed
 * natural size - so everything stacked with it measures from here rather than
 * picking its own number. The chart did that first (it is 100% of the panel); the
 * status banner was full-bleed `width: 100%` and read as a slab spanning the
 * monitor above a centred block.
 *
 * Counts the range card, which appears only once its series lands, so the column
 * does not resize under the reader when the fetch returns.
 */
export const SETTINGS_COLUMN_WIDTH = `${cardsRowWidth(GRAINS.length + 1)}px`;

export const DeliveryCard = ({ stats }) => (
  <Card style={{ flex: `0 1 ${CARD_WIDTH}px`, minWidth: '200px' }}>
    <CardContent>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '22px' }}>
        <Typography variant="overline" style={{ color: COLOR_MUTED, letterSpacing: '0.1em' }}>
          {stats.title}
        </Typography>
        {stats.paused && <PausedChip />}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>{double(stats.spend)}</Typography>
        {stats.hasCap && (
          <Typography variant="body2" style={{ color: COLOR_MUTED }}>of {double(stats.cap)}</Typography>
        )}
      </div>

      <CapBar stats={stats} />

      <div style={{ marginTop: '10px' }}>
        {/* Absent, not zero. The selected-range card has no impressions figure at
            all: the report endpoint returns one metric per request, so spend and
            impressions cannot arrive together. "0 impressions" beside a non-zero
            spend would be a straight contradiction. */}
        {stats.impressions != null && (
          <SecondaryLine>
            {long(stats.impressions)}
            {stats.impressionsCap ? ` of ${long(stats.impressionsCap)}` : ''} impressions
          </SecondaryLine>
        )}
        {stats.clicks !== null && stats.clicks !== undefined && (
          <SecondaryLine>
            {long(stats.clicks)} clicks
            {stats.ctr !== null ? ` · ${percent(100 * stats.ctr, 3)} CTR` : ''}
          </SecondaryLine>
        )}
        {stats.note && <SecondaryLine>{stats.note}</SecondaryLine>}
      </div>
    </CardContent>
  </Card>
);

/**
 * Stats for the selected-range card, from the chart's own series.
 *
 * Deliberately capless. The campaign's caps are hourly, daily and lifetime, and
 * none of them bounds an arbitrary range, so this card reports a total and says
 * plainly that there is nothing to measure it against. Reading it as cap progress
 * is the failure this wording exists to prevent.
 *
 * Returns null until the series has arrived: rangeTotal([]) is 0, and a confident
 * $0.00 on missing data is worse than an absent card.
 */
export function rangeCardStats (range) {
  // NOT a destructuring default: `= {}` only covers undefined, and the panel's
  // initial state is null, so this threw on the first render of every campaign
  // form before the tests caught it.
  const { label, total, ready } = range || {};
  if (!ready || !label) return null;
  return {
    key: 'range',
    title: label,
    spend: num(total),
    cap: null,
    hasCap: false,
    capNote: 'no cap for a custom range',
    pct: null,
    overCap: false,
    // Neither is available for a range: one metric per report request.
    impressions: null,
    impressionsCap: null,
    clicks: null,
    ctr: null,
    note: 'matches the chart above',
    paused: false
  };
}

/**
 * `record` is the campaign document. Rendered inside a FormDataConsumer on the
 * campaign Settings tab, so it receives formData rather than the raw record -
 * the two carry the same delivery counters.
 *
 * `rangeStats` is the optional fourth card, following the chart's range selector.
 * It is rendered in THIS row rather than beside the chart so all four squares
 * share one visual language, and so the row's own max-width accounts for it.
 */
const CampaignDeliveryCards = ({ record, rangeStats = null }) => {
  const cards = [
    ...GRAINS.map(grain => grainStats(record, grain)),
    ...(rangeStats ? [rangeStats] : [])
  ];
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${CARD_GAP}px`,
      // Belt and braces with the cards' own `flex: 0 1`: the row never spans a
      // wide monitor even if a card is later allowed to grow. Counts the cards
      // actually rendered, so the fourth one does not get squeezed out.
      maxWidth: `${cardsRowWidth(cards.length)}px`,
      marginBottom: '16px'
    }}>
      {cards.map(stats => <DeliveryCard key={stats.key} stats={stats} />)}
    </div>
  );
};

export default CampaignDeliveryCards;
